import { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { ListChecks, Plus, Trash2, X, CheckCircle, Circle, Search } from 'lucide-react';
import { COUNTRIES, COUNTRY_CONFIG } from '../../utils/visaConfig';

interface ChecklistTemplate {
  id: string;
  name: string;
  country: string;
  visa_type: string;
  items: any[];
  created_by: string;
  created_at: string;
  creator: {
    name: string;
  };
}

interface VisaAppointment {
  id: string;
  full_name: string;
  preferred_country: string;
  visit_purpose: string;
  application_number: string; // Assuming pnr or similar is used, or generate one
  passport_number: string;
}

interface ChecklistSummary {
  id?: string; // Optional because it might not exist yet
  visa_appointment_id: string;
  completed_count: number;
  total_count: number;
  appointment: VisaAppointment;
  items?: any[]; // For the detail view
}

export default function VisaChecklistManagement() {
  const [activeTab, setActiveTab] = useState<'templates' | 'applications'>('templates');
  const [templates, setTemplates] = useState<ChecklistTemplate[]>([]);
  const [checklists, setChecklists] = useState<ChecklistSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);

  // Detail Modal State
  const [selectedChecklist, setSelectedChecklist] = useState<ChecklistSummary | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  // Initialize with the first available country
  const defaultCountry = COUNTRIES[0]?.name || 'İTALYA';
  const defaultVisaType = COUNTRY_CONFIG[defaultCountry]?.visaTypes[0] || '';

  const [newTemplate, setNewTemplate] = useState({
    name: '',
    country: defaultCountry,
    visa_type: defaultVisaType,
    items: ['']
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    await Promise.all([fetchTemplates(), fetchChecklists()]);
    setLoading(false);
  };

  const fetchTemplates = async () => {
    try {
      const { data, error } = await supabase
        .from('visa_checklist_templates')
        .select(`
          *,
          creator:created_by(name)
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setTemplates(data || []);
      return data || [];
    } catch (error) {
      console.error('Error fetching templates:', error);
      return [];
    }
  };

  const fetchChecklists = async () => {
    try {
      // 1. Fetch Appointments with status 'Kontrol'
      const { data: appointments, error: appError } = await supabase
        .from('visa_appointments')
        .select('id, full_name, preferred_country, visit_purpose, passport_number')
        .eq('status', 'Kontrol');

      if (appError) throw appError;

      if (!appointments || appointments.length === 0) {
        setChecklists([]);
        return;
      }

      // 2. Fetch Existing Checklists for these appointments
      const appointmentIds = appointments.map(a => a.id);
      const { data: existingChecklists, error: checkError } = await supabase
        .from('visa_checklists')
        .select('*')
        .in('visa_appointment_id', appointmentIds);

      if (checkError) throw checkError;

      // 3. Fetch Templates (to match if no checklist exists)
      // We can use the state 'templates' if it's already set, but fetchData calls them in parallel.
      // So we might need to fetch them again or wait.
      // For simplicity in this logic, let's fetch strictly what we need or assume fetchTemplates finishes?
      // Better: fetch templates here locally or ensure we have them. 
      // Reuse the templates fetching logic slightly or just fetch all templates again to be safe/consistent for now.
      const { data: allTemplates } = await supabase.from('visa_checklist_templates').select('*');

      // 4. Merge Data
      const mergedData: ChecklistSummary[] = appointments.map(app => {
        const existing = existingChecklists?.find(c => c.visa_appointment_id === app.id);

        let total = 0;
        let completed = 0;
        let items = [];

        if (existing) {
          total = existing.total_count;
          completed = existing.completed_count;
          items = existing.items;
        } else {
          // Find matching template
          const template = allTemplates?.find(t =>
            t.country === app.preferred_country &&
            t.visa_type === app.visit_purpose
          );
          if (template) {
            total = template.items.length;
            items = template.items.map((item: any) => ({ ...item, completed: false })); // Ensure items from template are marked as not completed
          }
        }

        return {
          id: existing?.id, // undefined if not started
          visa_appointment_id: app.id,
          completed_count: completed,
          total_count: total,
          items: items,
          appointment: {
            id: app.id,
            full_name: app.full_name,
            preferred_country: app.preferred_country,
            visit_purpose: app.visit_purpose,
            application_number: app.passport_number, // User pnr or pass no as ID visual
            passport_number: app.passport_number
          }
        };
      });

      setChecklists(mergedData);
    } catch (error) {
      console.error('Error fetching checklists:', error);
    }
  };

  const handleAddTemplate = async () => {
    if (!newTemplate.name.trim()) {
      alert('Lütfen şablon adı girin');
      return;
    }

    const validItems = newTemplate.items.filter(item => item.trim() !== '');
    if (validItems.length === 0) {
      alert('Lütfen en az bir öğe ekleyin');
      return;
    }

    try {
      const { data: { user } } = await supabase.auth.getUser();

      const templateItems = validItems.map(item => ({
        id: crypto.randomUUID(),
        title: item.trim(),
        completed: false
      }));

      const { error } = await supabase
        .from('visa_checklist_templates')
        .insert({
          name: newTemplate.name.trim(),
          country: newTemplate.country,
          visa_type: newTemplate.visa_type,
          items: templateItems,
          created_by: user?.id
        });

      if (error) throw error;

      setShowAddModal(false);
      setNewTemplate({
        name: '',
        country: defaultCountry,
        visa_type: defaultVisaType,
        items: ['']
      });
      await fetchTemplates();
    } catch (error) {
      console.error('Error adding template:', error);
      alert('Şablon eklenirken hata oluştu');
    }
  };

  const handleDeleteTemplate = async (id: string) => {
    if (!await window.appConfirm('Bu şablonu silmek istediğinizden emin misiniz?')) return;

    try {
      const { error } = await supabase
        .from('visa_checklist_templates')
        .delete()
        .eq('id', id);

      if (error) throw error;
      await fetchTemplates();
    } catch (error) {
      console.error('Error deleting template:', error);
      alert('Şablon silinirken hata oluştu');
    }
  };

  const toggleChecklistItem = async (summary: ChecklistSummary, itemId: string, currentStatus: boolean) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      // If no ID, we need to Create the checklist first
      let checklistId = summary.id;
      let currentItems = summary.items || [];

      if (!checklistId) {
        // Create new checklist
        const newItems = currentItems.map(i => i.id === itemId ? { ...i, completed: !currentStatus } : i);
        const completedCount = newItems.filter((i: any) => i.completed).length;

        const { data: newChecklist, error: createError } = await supabase
          .from('visa_checklists')
          .insert({
            visa_appointment_id: summary.visa_appointment_id,
            items: newItems,
            total_count: newItems.length,
            completed_count: completedCount,
            created_by: user?.id // Assuming created_by is a column
          })
          .select()
          .single();

        if (createError) throw createError;
        checklistId = newChecklist.id;
        currentItems = newChecklist.items;
      } else {
        // Update existing
        const newItems = currentItems.map(i =>
          i.id === itemId ? { ...i, completed: !currentStatus } : i
        );
        const completedCount = newItems.filter(i => i.completed).length;

        const { error: updateError } = await supabase
          .from('visa_checklists')
          .update({
            items: newItems,
            completed_count: completedCount,
            updated_at: new Date().toISOString()
          })
          .eq('id', checklistId);

        if (updateError) throw updateError;
        currentItems = newItems;
      }

      // Update Local State without Refetching entire list for speed
      setChecklists(prev => prev.map(c => {
        if (c.visa_appointment_id === summary.visa_appointment_id) {
          return {
            ...c,
            id: checklistId,
            items: currentItems,
            completed_count: currentItems.filter((i: any) => i.completed).length
          };
        }
        return c;
      }));

      // Also update selected checklist if modal is open
      if (selectedChecklist?.visa_appointment_id === summary.visa_appointment_id) {
        setSelectedChecklist(prev => prev ? ({
          ...prev,
          id: checklistId,
          items: currentItems,
          completed_count: currentItems.filter((i: any) => i.completed).length
        }) : null);
      }

    } catch (error) {
      console.error('Error toggling item:', error);
      alert('İşlem sırasında hata oluştu');
    }
  };

  const filteredTemplates = templates.filter(t =>
    t.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    t.country.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredChecklists = checklists.filter(c =>
    c.appointment.full_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.appointment.preferred_country?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getProgressColor = (completed: number, total: number) => {
    if (total === 0) return 'text-gray-400';
    const percentage = (completed / total) * 100;
    if (percentage === 100) return 'text-green-600';
    if (percentage >= 70) return 'text-blue-600';
    if (percentage >= 40) return 'text-yellow-600';
    return 'text-red-600';
  };

  return (
    <div className="p-8">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Kontrol Listeleri</h1>
          <p className="mt-2 text-gray-600 dark:text-gray-400">Şablonları yönetin ve başvuru ilerlemelerini takip edin</p>
        </div>
        {activeTab === 'templates' && (
          <button
            onClick={() => setShowAddModal(true)}
            className="flex items-center gap-2 px-6 py-3 bg-[#0c9346] text-white rounded-lg hover:bg-[#0a7a39] transition-colors"
          >
            <Plus className="w-5 h-5" />
            Yeni Şablon
          </button>
        )}
      </div>

      <div className="border-b border-gray-200 dark:border-gray-700 mb-6">
        <div className="flex gap-4">
          <button
            onClick={() => setActiveTab('templates')}
            className={`py-4 px-6 border-b-2 font-medium transition-colors ${activeTab === 'templates'
              ? 'border-[#0c9346] text-[#0c9346]'
              : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
              }`}
          >
            <ListChecks className="w-5 h-5 inline-block mr-2" />
            Şablonlar ({templates.length})
          </button>
          <button
            onClick={() => setActiveTab('applications')}
            className={`py-4 px-6 border-b-2 font-medium transition-colors ${activeTab === 'applications'
              ? 'border-[#0c9346] text-[#0c9346]'
              : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
              }`}
          >
            <CheckCircle className="w-5 h-5 inline-block mr-2" />
            Başvurular ({checklists.length})
          </button>
        </div>
      </div>

      <div className="mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          <input
            type="text"
            placeholder={activeTab === 'templates' ? 'Şablon ara...' : 'Öğrenci adı veya ülke ara...'}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
          />
        </div>
      </div>

      {activeTab === 'templates' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {loading ? (
            <div className="col-span-full text-center py-12 text-gray-500 dark:text-gray-400">
              Yükleniyor...
            </div>
          ) : filteredTemplates.length === 0 ? (
            <div className="col-span-full text-center py-12 text-gray-500 dark:text-gray-400">
              Şablon bulunamadı
            </div>
          ) : (
            filteredTemplates.map((template) => (
              <div
                key={template.id}
                className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">
                      {template.name}
                    </h3>
                    <div className="flex flex-wrap gap-2 text-sm text-gray-600 dark:text-gray-400">
                      <span className="px-2 py-1 bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 rounded">
                        {template.country}
                      </span>
                      <span className="px-2 py-1 bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200 rounded capitalize">
                        {template.visa_type}
                      </span>
                    </div>
                  </div>
                  <button
                    onClick={() => handleDeleteTemplate(template.id)}
                    className="p-2 hover:bg-red-100 dark:hover:bg-red-900 rounded transition-colors"
                  >
                    <Trash2 className="w-4 h-4 text-red-600" />
                  </button>
                </div>

                <div className="space-y-2 mb-4">
                  {template.items.slice(0, 5).map((item: any, index: number) => (
                    <div key={index} className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                      <Circle className="w-4 h-4 text-gray-400" />
                      <span className="truncate">{item.title}</span>
                    </div>
                  ))}
                  {template.items.length > 5 && (
                    <p className="text-sm text-gray-500 dark:text-gray-400 ml-6">
                      +{template.items.length - 5} daha...
                    </p>
                  )}
                </div>

                <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600 dark:text-gray-400">
                      {template.items.length} öğe
                    </span>
                    <span className="text-gray-500 dark:text-gray-500">
                      {template.creator?.name}
                    </span>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {activeTab === 'applications' && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-gray-900">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Öğrenci
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Pasaport No
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Ülke & Vize
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    İlerleme
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Özet
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    İşlem
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {loading ? (
                  <tr>
                    <td colSpan={6} className="px-6 py-12 text-center text-gray-500 dark:text-gray-400">
                      Yükleniyor...
                    </td>
                  </tr>
                ) : filteredChecklists.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-6 py-12 text-center text-gray-500 dark:text-gray-400">
                      Bekleyen "Kontrol" durumunda başvuru bulunamadı.
                    </td>
                  </tr>
                ) : (
                  filteredChecklists.map((checklist) => {
                    const percentage = checklist.total_count > 0
                      ? Math.round((checklist.completed_count / checklist.total_count) * 100)
                      : 0;

                    return (
                      <tr
                        key={checklist.visa_appointment_id}
                        className="hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer"
                        onClick={() => {
                          setSelectedChecklist(checklist);
                          setShowDetailModal(true);
                        }}
                      >
                        <td className="px-6 py-4 text-sm font-medium text-gray-900 dark:text-white">
                          {checklist.appointment.full_name}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900 dark:text-white">
                          {checklist.appointment.passport_number || '-'}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900 dark:text-white">
                          <div className="flex flex-col">
                            <span className="font-medium">{checklist.appointment.preferred_country || '-'}</span>
                            <span className="text-gray-500 text-xs">{checklist.appointment.visit_purpose || '-'}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            <div className="flex-1 bg-gray-200 dark:bg-gray-700 rounded-full h-2 min-w-[100px]">
                              <div
                                className="bg-[#0c9346] h-2 rounded-full transition-all duration-300"
                                style={{ width: `${percentage}%` }}
                              ></div>
                            </div>
                            <span className="text-sm text-gray-600 dark:text-gray-400 w-12">
                              {percentage}%
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <span className={`text-sm font-medium ${getProgressColor(checklist.completed_count, checklist.total_count)}`}>
                            {checklist.completed_count} / {checklist.total_count}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <button className="text-blue-600 hover:text-blue-800 text-sm">
                            Detay
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Yeni Şablon</h2>
              <button
                onClick={() => setShowAddModal(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Şablon Adı
                </label>
                <input
                  type="text"
                  value={newTemplate.name}
                  onChange={(e) => setNewTemplate({ ...newTemplate, name: e.target.value })}
                  placeholder="Örn: İtalya Öğrenci Vizesi Kontrol Listesi"
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Ülke
                  </label>
                  <select
                    value={newTemplate.country}
                    onChange={(e) => {
                      const country = e.target.value;
                      const visaTypes = COUNTRY_CONFIG[country]?.visaTypes || [];
                      setNewTemplate({
                        ...newTemplate,
                        country,
                        visa_type: visaTypes[0] || ''
                      });
                    }}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  >
                    {COUNTRIES.map(c => (
                      <option key={c.name} value={c.name}>{c.name}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Vize Türü
                  </label>
                  <select
                    value={newTemplate.visa_type}
                    onChange={(e) => setNewTemplate({ ...newTemplate, visa_type: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  >
                    {COUNTRY_CONFIG[newTemplate.country]?.visaTypes.map(vt => (
                      <option key={vt} value={vt}>{vt}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Kontrol Listesi Öğeleri
                </label>
                <div className="space-y-2">
                  {newTemplate.items.map((item, index) => (
                    <div key={index} className="flex gap-2">
                      <input
                        type="text"
                        value={item}
                        onChange={(e) => {
                          const newItems = [...newTemplate.items];
                          newItems[index] = e.target.value;
                          setNewTemplate({ ...newTemplate, items: newItems });
                        }}
                        placeholder={`Öğe ${index + 1}`}
                        className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      />
                      {index > 0 && (
                        <button
                          onClick={() => {
                            const newItems = newTemplate.items.filter((_, i) => i !== index);
                            setNewTemplate({ ...newTemplate, items: newItems });
                          }}
                          className="p-2 hover:bg-red-100 dark:hover:bg-red-900 rounded"
                        >
                          <X className="w-5 h-5 text-red-600" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
                <button
                  onClick={() => setNewTemplate({ ...newTemplate, items: [...newTemplate.items, ''] })}
                  className="mt-2 flex items-center gap-2 px-4 py-2 text-sm text-[#0c9346] hover:bg-green-50 dark:hover:bg-green-900 rounded-lg transition-colors"
                >
                  <Plus className="w-4 h-4" />
                  Öğe Ekle
                </button>
              </div>
            </div>

            <div className="flex justify-end gap-2 p-6 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={() => setShowAddModal(false)}
                className="px-6 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                İptal
              </button>
              <button
                onClick={handleAddTemplate}
                className="px-6 py-2 bg-[#0c9346] text-white rounded-lg hover:bg-[#0a7a39]"
              >
                Oluştur
              </button>
            </div>
          </div>
        </div>
      )}

      {showDetailModal && selectedChecklist && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
              <div>
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                  {selectedChecklist.appointment.full_name}
                </h2>
                <p className="text-sm text-gray-500 mt-1">
                  {selectedChecklist.appointment.preferred_country} - {selectedChecklist.appointment.visit_purpose}
                </p>
              </div>
              <button
                onClick={() => setShowDetailModal(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6">
              {(!selectedChecklist.items || selectedChecklist.items.length === 0) ? (
                <div className="text-center py-12 text-gray-500">
                  <ListChecks className="w-12 h-12 mx-auto mb-3 opacity-20" />
                  <p className="text-lg font-medium">Bu başvuru tipi için eşleşen şablon bulunamadı.</p>
                  <p className="text-sm mt-2">Lütfen "{selectedChecklist.appointment.preferred_country}" ülkesi ve "{selectedChecklist.appointment.visit_purpose}" vize türü için bir şablon oluşturun.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="flex items-center justify-between mb-4 bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
                    <span className="font-medium text-blue-900 dark:text-blue-100">İlerleme Durumu</span>
                    <span className="text-blue-700 dark:text-blue-300 font-bold">
                      {selectedChecklist.completed_count} / {selectedChecklist.total_count}
                    </span>
                  </div>

                  {selectedChecklist.items.map((item: any) => (
                    <div
                      key={item.id}
                      className={`flex items-center p-3 rounded-lg border transition-all cursor-pointer ${item.completed
                        ? 'bg-green-50 border-green-200 dark:bg-green-900/10 dark:border-green-800'
                        : 'bg-white border-gray-200 hover:border-blue-300 dark:bg-gray-800 dark:border-gray-700'
                        }`}
                      onClick={() => toggleChecklistItem(selectedChecklist, item.id, item.completed)}
                    >
                      <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center mr-3 transition-colors ${item.completed
                        ? 'bg-[#0c9346] border-[#0c9346]'
                        : 'border-gray-300 dark:border-gray-500'
                        }`}>
                        {item.completed && <CheckCircle className="w-4 h-4 text-white" />}
                      </div>
                      <span className={`text-base ${item.completed ? 'text-gray-500 line-through' : 'text-gray-900 dark:text-white'}`}>
                        {item.title}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
